import { TablePaginationConfig } from 'antd';
import { FilterValue, RowSelectionType, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import React, { Key, ReactType, useCallback, useMemo, useState } from 'react';
import { INIT_PAGESIZE } from 'commons/constants';

export interface PageInfoType {
  pageNo: number;
  pageSize: number;
}
/**
 *  pageInfo: 현재 페이지와 한 페이지에 표시할 row 갯수
 *  datas: 테이블에 뿌려질 전체 데이터 배열
 *  onChangePageInfo: 페이지네이션 버튼이 눌렸을때 발생하는 바껴야할 페이지정보
 *  filterKey: 컬럼에 적용된 필터 키 리스트
 */
interface useTableInfoProps {
  pageInfo: PageInfoType | undefined;
  datas?: any[];
  tablePaginationConfig?: TablePaginationConfig;
  onChangePageInfo?: (page: number, pageSize: number) => void;
  onChangeFilters?: (filters: Record<string, FilterValue | null> | undefined) => void;
  onChangeSorters?: (sorter: SorterResult<any> | SorterResult<any>[]) => void;
  filterKey?: string[];
  selectionType?: RowSelectionType;
  totalCount?: number;
  disabledKeys?: React.Key[] | number[];
  rowSelectLimit?: number;
  preserveSelectedRowKeys?: boolean;
  showSizeChanger?: boolean;
}

const useTableInfo = ({
  pageInfo,
  datas,
  tablePaginationConfig,
  onChangePageInfo,
  filterKey,
  onChangeFilters,
  onChangeSorters,
  selectionType,
  totalCount,
  disabledKeys,
  rowSelectLimit,
  preserveSelectedRowKeys = false,
  showSizeChanger = true,
}: useTableInfoProps) => {
  /* ======================================================
   * state
   * ====================================================== */
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<Key>>([]);
  const [searching, setSearching] = useState<boolean>();

  /* ======================================================
   * variables
   * ====================================================== */
  const rowSelectionType: RowSelectionType = selectionType || 'checkbox';
  const pagination = useMemo((): TablePaginationConfig | undefined => {
    if (pageInfo && datas) {
      return {
        showSizeChanger: showSizeChanger,
        pageSize: pageInfo?.pageSize || INIT_PAGESIZE,
        current: pageInfo?.pageNo,
        total: totalCount,
        hideOnSinglePage: true,
        pageSizeOptions: [15, 30, 50, 100],
        ...tablePaginationConfig,
      };
    }

    return { total: 0 };
  }, [pageInfo, datas]);
  /* ======================================================
   * function
   * ====================================================== */
  const changePageNation = useCallback(
    (cur, size) => {
      if (!preserveSelectedRowKeys) setSelectedRowKeys([]);
      if (onChangePageInfo) {
        onChangePageInfo(cur, size);
      }
    },
    [pagination, onChangePageInfo],
  );

  const rowSelection = {
    type: rowSelectionType,
    selectedRowKeys,
    onChange: (selectedKeys: Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: rowSelectLimit
        ? (selectedRowKeys.length + (disabledKeys?.length || 0) >= rowSelectLimit &&
            !selectedRowKeys.includes(record.key)) ||
          disabledKeys?.includes(+record.key)
        : disabledKeys?.includes(+record.key),
    }),
    preserveSelectedRowKeys: preserveSelectedRowKeys,
  };

  const changeFilter = useCallback(
    (filters: Record<string, FilterValue | null> | undefined) => {
      if (filters) {
        if (filterKey) {
          setSelectedRowKeys([]);
          const result = filterKey.some((value: string) => !!filters[value]);

          setSearching(result);
          return;
        }

        setSearching(false);
      } else {
        setSearching(false);
      }
    },
    [setSearching, filterKey],
  );

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
    extra: TableCurrentDataSource<any>,
  ) => {
    if (extra.action === 'filter') onChangeFilters?.(filters);
    if (extra.action === 'sort') onChangeSorters?.(sorter);

    if (filters) {
      changeFilter?.(filters);
    } else {
      changeFilter?.(undefined);
    }

    if (extra.action === 'paginate') {
      if (pagination.current && pagination.pageSize) {
        changePageNation?.(pagination.current, pagination.pageSize);
      }
    }
  };
  const rowClassName = (record: any, index: number) => {
    return (selectedRowKeys.length + (disabledKeys?.length || 0) >= (rowSelectLimit || 9999999999) &&
      !selectedRowKeys.includes(record.key)) ||
      disabledKeys?.includes(+record.key)
      ? 'disableRow'
      : 'ableRow';
  };
  return {
    selectedRowKeys,
    setSelectedRowKeys,
    searching,
    setSearching,
    pagination,
    changePageNation,
    rowSelection,
    changeFilter,
    onTableChange,
    rowClassName,
  };
};

export default useTableInfo;
